/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOpenPopup';

const ActionOpenPopupManager: ActionManagerFn<'ActionOpenPopup'> = (
  platform: Platform,
): ActionManager<'ActionOpenPopup'> => {
  return {
    ...baseActionManager(platform, 'ActionOpenPopup'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Popup Item ID',
        type: 'ItemID',
        initialFilter: 'Popup',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Popup') {
            return `Item id '${value}' doesn't belong to a Popup`;
          }
          return '';
        },
      },
    },
    name: 'Open Popup',
    replaceableWith: ['ActionClosePopup'],
    isItemIdUsed: (item, itemId) => item.props.targetItemId === itemId,
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'window-restore' },
    description: `**Overview**<br/>
The Open Popup action is a UI interaction component designed to programmatically activate a specific "Popup" (modal) component on the page. Unlike Drawers (side panels) or Popovers (context overlays), a Popup typically centers itself on the screen and often acts as a modal dialog that demands user attention before returning to the main content.<br/><br/>
This action functions as the trigger mechanism to reveal these dialogs. It is tightly coupled with the Popup component type, utilizing strict validation logic to ensure that the target ID provided specifically references a valid Popup element, preventing configuration errors. It is designed to be easily swappable with the "Close Popup" action to streamline logic adjustments.

**Key Features**<br/>
* **Targeted Activation:** Specifically designed to trigger the display logic of a modal Popup component.
* **Strict Type Validation:** Implements validateItemId to verify that the selected targetItemId is explicitly a Popup. If a different container type is selected, the builder validates against it.
* **Registry Filtering:** The property picker uses initialFilter: 'Popup', streamlining the configuration process by restricting the dropdown list to show only valid popup components available on the page.
* **Component Interchangeability:** Explicitly configured in replaceableWith to be easily swapped with ActionClosePopup, allowing developers to toggle between opening and closing logic efficiently.
* **Dependency Tracking:** Implements isItemIdUsed to correctly register the dependency between the action and the target Popup, ensuring the platform understands the relationship for validation and refactoring.

**Properties**<br/>
* **Popup Item ID (targetItemId):** A property of type ItemID.
  * *Function:* Identifies the unique ID of the Popup component to be opened.
  * *Validation:* Must reference a valid component of type 'Popup'.

**Use Cases**<br/>
* **Data Entry:** Clicking an "Add Record" button to open a Popup containing a form for creating a new entry without leaving the list view.
* **Detail Views:** A "Quick View" button on a product card that opens a modal with more details about the item.
* **Custom Alerts:** Triggering a custom-designed Popup message (instead of a standard browser alert) to inform the user of a system state or error.`,
  };
};

export default ActionOpenPopupManager;
